import React from "react";
import { useIntl } from "react-intl";
import Card from "../Card/Card";
import { Icon } from "@chakra-ui/react";
import { LuUsers } from "react-icons/lu";
import { IoMdCheckmarkCircleOutline } from "react-icons/io";
import { Box, Text } from "@chakra-ui/react";

interface SharedWithAllCustomersCardProps {
  sharedByAdminCount: number;
  sharedByAdminTimestamp: number;
}

export default function SharedWithAllCustomersCard({
  sharedByAdminCount,
  sharedByAdminTimestamp,
}: SharedWithAllCustomersCardProps) {
  const intl = useIntl();

  return (
    <Card mb={3} bg="white" boxShadow="sm" border="none" ml={-5}>
      <Box display="flex" flexDirection="column" alignItems="flex-start">
        <Box display="flex" alignItems="center" gap={3}>
          <Box
            borderRadius="full"
            boxSize="32px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon as={IoMdCheckmarkCircleOutline} color="#2fc867" boxSize={6} />
          </Box>
          <Text
            fontWeight="500"
            color="black.900"
            fontSize="16px"
            sx={{ fontWeight: 600 }}
          >
            {intl.formatMessage({
              id: "sharing.sharedWithAllCustomers",
              defaultMessage: "Shared with all customers",
            })}
          </Text>
          <Box>
            <Box
              as="span"
              bg="#dbeafe"
              color="blue.600"
              fontWeight="600"
              px={3}
              py={1}
              ml={3}
              borderRadius="20px"
              fontSize="15px"
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <Icon
                as={LuUsers}
                color="blue.600"
                boxSize={4}
                style={{ marginRight: "8px" }}
              />
              {sharedByAdminCount?.toLocaleString()}
            </Box>
          </Box>
        </Box>

        <Text color="gray.500" fontSize="14px" mt={2} ml="42px">
          {sharedByAdminTimestamp
            ? getLocalizedTimeAgo(sharedByAdminTimestamp, intl)
            : ""}
        </Text>
      </Box>
    </Card>
  );
}

const getLocalizedTimeAgo = (timestamp: number, intl: any) => {
  const now = Date.now();
  const diffMs = now - timestamp;
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / (60 * 60000));
  const diffDays = Math.floor(diffMs / (24 * 60 * 60000));
  const diffWeeks = Math.floor(diffMs / (7 * 24 * 60 * 60000));
  const diffMonths = Math.floor(diffMs / (30 * 24 * 60 * 60000));

  let value = 0;
  let unit = "minute";
  if (diffMinutes < 60) {
    value = diffMinutes;
    unit = value === 1 ? "minute" : "minutes";
  } else if (diffHours < 24) {
    value = diffHours;
    unit = value === 1 ? "hour" : "hours";
  } else if (diffDays < 7) {
    value = diffDays;
    unit = value === 1 ? "day" : "days";
  } else if (diffWeeks < 4) {
    value = diffWeeks;
    unit = value === 1 ? "week" : "weeks";
  } else {
    value = diffMonths;
    unit = value === 1 ? "month" : "months";
  }

  // Fallback for translation keys
  const unitKeyMap: Record<string, string> = {
    minute: "minute",
    minutes: "subscription.minutes",
    hour: "hour",
    hours: "subscription.hours",
    day: "day",
    days: "subscription.days",
    week: "week",
    weeks: "weeks",
    month: "month",
    months: "months",
  };

  // Try to use translation key, fallback to unit
  const unitLabel = intl.formatMessage({
    id: unitKeyMap[unit] || unit,
    defaultMessage: unit,
  });

  // No translation key for 'ago', so use a default message
  return `${intl.locale === "ar" ? "منذ" : ""} ${value} ${unitLabel} ${
    intl.locale === "ar" ? "" : "ago"
  }`;
};
